import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Car, Hotel, MapPin, Star } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const formatPrice = (min, max, currency = 'GBP') => {
  if (min == null && max == null) return null;
  const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
  const fmt = (n) => `${symbol}${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (min != null && max != null && Number(min) === Number(max)) return fmt(min);
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  return fmt(min ?? max);
};

const PackagesSection = () => {
  const { t, currentLanguage } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/packages`, { params: { lang: currentLanguage } });
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const sorted = [...list].sort((a, b) => {
          if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
          return (a.sort_order || 0) - (b.sort_order || 0);
        });
        setPackages(sorted.slice(0, 3));
      } catch (err) {
        console.error('Failed to load packages:', err);
        if (!cancelled) setPackages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  if (!loading && packages.length === 0) return null;

  return (
    <section id="packages" className="section-padding section-tone-e">
      <div className="container-premium">
        <div className="section-head">
          <span className="section-eyebrow">{t('packagesSection.eyebrow', 'All-in Packages')}</span>
          <h2 className="section-title">{t('packagesSection.title', 'Treatment Packages')}</h2>
          <p className="section-sub">
            {t('packagesSection.subtitle', 'Treatment + 4★/5★ hotel + transfers + sightseeing — at a fraction of UK prices.')}
          </p>
        </div>

        <div className="grid-3 packages-grid">
          {(loading ? [0, 1, 2] : packages).map((pkg, idx) => {
            if (loading) {
              return (
                <article key={`s-${idx}`} className="package-card-rich is-skeleton">
                  <div className="skeleton-line w-40" style={{ height: 18 }} />
                  <div className="skeleton-line w-80" style={{ height: 24, marginTop: 12 }} />
                  <div className="skeleton-line w-60" style={{ marginTop: 24 }} />
                  <div className="skeleton-line w-50" />
                  <div className="skeleton-line w-70" style={{ marginTop: 16 }} />
                </article>
              );
            }
            const trPrice = formatPrice(pkg.price_tr_min, pkg.price_tr_max, pkg.price_currency);
            const ukPrice = formatPrice(pkg.price_uk_min, pkg.price_uk_max, pkg.price_currency);
            return (
              <article
                key={pkg.id || pkg.slug}
                className={`package-card-rich ${pkg.is_featured ? 'is-featured' : ''}`}
              >
                {pkg.is_featured ? (
                  <span className="package-tag tag-gold">
                    <Star size={12} /> {t('packages.featured', 'Featured')}
                  </span>
                ) : null}
                {pkg.savings_percent ? (
                  <span className="package-tag tag-green">
                    -{pkg.savings_percent}% {t('packages.vs_uk', 'vs UK')}
                  </span>
                ) : null}

                <h3 className="package-title">{pkg.title}</h3>
                {pkg.short_description && (
                  <p className="package-sub">{pkg.short_description}</p>
                )}

                <ul className="package-feats">
                  {pkg.includes_hotel ? (
                    <li><Hotel size={13} /> {t('packages.hotel', 'Hotel')}</li>
                  ) : null}
                  {pkg.includes_transfer ? (
                    <li><Car size={13} /> {t('packages.transfer', 'Transfer')}</li>
                  ) : null}
                  {pkg.includes_sightseeing ? (
                    <li><MapPin size={13} /> {t('packages.sightseeing', 'Tour')}</li>
                  ) : null}
                </ul>

                <div className="package-price-block">
                  {ukPrice ? (
                    <span className="package-uk">
                      {t('packages.uk_label', 'UK')} <s>{ukPrice}</s>
                    </span>
                  ) : null}
                  {trPrice ? (
                    <span className="package-tr">{trPrice}</span>
                  ) : null}
                  <span className="package-from">{t('packagesSection.fromLabel', 'from')}</span>
                </div>

                <div className="package-cta-row">
                  <Link to={`/packages/${pkg.slug}`} className="btn-crystal-outline">
                    {t('common.learn_more', 'Learn more')}
                  </Link>
                  <Link to="/appointment" className="btn-crystal">
                    {t('packagesSection.ctaQuote', 'Get a quote')} <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        <div className="section-foot">
          <Link to="/packages" className="btn-crystal-outline">
            {t('packagesSection.viewAll', 'View all packages')} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
